import {
    AfterContentInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UnsubscribeCallback } from '@se/platform.custom.store';
import { MediaService } from '../../../lib/media.service';
import { Hotkey, HotkeysService } from 'angular2-hotkeys';
import { Subscription } from 'rxjs';
import { AppConfig } from '../../../app/app.config';
import { EcgConfiguration } from '../../../app/resolvers/ecg-configuration';
import { ArrangementType, CaliperMode, Format, GuidedComparisonType } from '../../models/enums';
import { CustomCommandState } from '../../view-models/ecgstate';
import { CultureMappingService } from '../../utils/culture.service';
import { ECG_CONSTANTS } from '../../utils/ecg-constants';
import { KeyValuePair } from '../../utils/key-value-pair';
import { PostMessage, PostMessageService, PostMessageType } from '../../utils/post-message.service';
import { IArrangementConfiguration } from '../../models/interfaces';
import { select, Store } from '@ngrx/store';
import { ecgViewerActions } from 'projects/ecg-viewer-html5/src/store/actions';
import {
    selectorCaliperMode,
    selectorCulture,
    selectorCustomCommand,
    selectorDetailsVisible,
    selectorFiducialPointsVisible,
    selectorFormat,
    selectorFullState,
    selectorHorizontalStudySpacing,
    selectorIsFitBaseline,
    selectorPaperOpacity,
    selectorPaperRate,
    selectorPrintAsDisplayed,
    selectorStudySpacing,
    selectorSummaryVisible,
    selectorTimeAlignment,
    selectorTraceSpacing,
    selectorZoomFactor,
} from 'projects/ecg-viewer-html5/src/store/selectors/ecg-viegew.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { ECGState } from 'projects/ecg-viewer-html5/src/store/reducers/ecg-viewer.reducer';
import { GuidedComparisonMessageParam } from 'projects/ecg-viewer-html5/src/store/ecg-viewer-state.models';
import { LayoutFabSubToolsComponent } from './layout-fab-sub-tools/layout-fab-sub-tools.component';
import { FormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { NgForOf, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { SeFabSliderComponent } from '../fab-components/se-fab-slider/se-fab-slider.component';
import { MatButton, MatButtonModule } from '@angular/material/button';

export interface SchillerFilters {
    lowFilter?: number | null;
    highFilter?: number | null;
}

export type HK = Hotkey | Hotkey[] | null;

@Component({
    selector: 'layout-fab-tools',
    templateUrl: './layout-fab-tools.component.html',
    styleUrls: ['./layout-fab-tools.component.scss'],
    encapsulation: ViewEncapsulation.None,
    viewProviders: [PostMessageService],
    standalone: true,
    imports: [
        LayoutFabSubToolsComponent,
        TranslatePipe,
        MatButtonToggleGroup,
        FormsModule,
        MatButtonToggle,
        MatButtonModule,
        NgIf,
        MatIcon,
        NgForOf,
        MatTooltip,
        SeFabSliderComponent,
    ],
})
export class LayoutFabToolsComponent implements OnChanges, AfterContentInit, OnDestroy {
    // Data Members

    isFabOpen: boolean = false;
    readonly globalInputValue = 'global';
    readonly leadInputValue = 'lead';
    @Output()
    fullscreenChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() lowFilterChange: EventEmitter<number> = new EventEmitter<number>();
    @Input()
    chartLayersNumber: number = 0;
    @Input()
    arrangementConfiguration!: IArrangementConfiguration;
    @Input()
    ecgConfiguration!: EcgConfiguration;
    @Output()
    ecgConfigurationChange = new EventEmitter<EcgConfiguration>();
    formats: Array<number> = [];
    @Input()
    enableStudiesSpacing = false;
    @Input() enabledPrintAsDisplayed: boolean = false;
    caliperModes: Array<number> = [];
    @Input() isEcgReportingDetail: boolean = false; // non mostrare sotto menu fab-tool per misure globali/singolo canale. il mat button rounded va di default su misure per singolo canale se presenti vedi bug-101740
    arrangements: Array<KeyValuePair<string, number>> = [];
    timeAlignments: Array<number> = [];
    paperRates: Array<number> = [];
    guidedComparisons: Array<KeyValuePair<string, number>> = [];
    isShowingLeads = false;
    @Input() showLeadsAnnotationsButton: boolean = false;
    @Input() showGlobalAnnotationsButton: boolean = false;
    @Output() customMeasuresChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() leadsAnnotationsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    public enableSchillerFilters = false;
    public schillerFiltersConfiguration = {
        lowFilters: [0, 25, 40, 150],
    };
    public lowFilter: number = 0;
    public selectedMeasures: string[] = [];
    subscriptions: Subscription[] = [];
    private storeSubscriber: UnsubscribeCallback | null = null;
    private translateSubscriber: Subscription | null = null;
    // hotkeys
    private hkFullScreen: HK = null;
    private hkArrangement: HK = null;
    private hkFormat: HK = null;
    private hkTime: HK = null;
    private hkRate: HK = null;
    private hkCaliperMode: HK = null;
    private hkFiducialPoints: HK = null;
    private hkOpacityUp: HK = null;
    private hkOpacityDown: HK = null;
    private hkTraceSpacingUp: HK = null;
    private hkTraceSpacingDown: HK = null;
    private hkStudySpacingUp: HK = null;
    private hkStudySpacingDown: HK = null;

    constructor(
        public mediaService: MediaService,
        private postMessageService: PostMessageService,
        private cultureMappingService: CultureMappingService,
        private translate: TranslateService,
        private appConfig: AppConfig,
        private hotkeysService: HotkeysService,
        private ecgStore: Store<ECGState>,
        private actions: Actions,
    ) {
        this.addHotKeys();
    }

    private _isFullscreen: boolean = false;

    get isFullscreen() {
        return this._isFullscreen;
    }

    @Input()
    set schillerFilters(filters: SchillerFilters) {
        this.lowFilter = filters?.lowFilter ?? 0;
        this.enableSchillerFilters = !!Object.keys(filters ?? {}).length;
    }

    get culture() {
        return this.ecgStore.selectSignal(selectorCulture)();
    }

    @Input() set culture(culture: number) {
        this.ecgStore.dispatch(ecgViewerActions.cultureMessage({ culture: culture }));
    }

    get paperOpacity() {
        return this.ecgStore.selectSignal(selectorPaperOpacity)();
    }

    @Input()
    set paperOpacity(paperOpacity: number) {
        this.ecgStore.dispatch(ecgViewerActions.paperOpacityMessage({ paperOpacity: paperOpacity }));
    }

    get traceSpacing() {
        return this.ecgStore.selectSignal(selectorTraceSpacing)();
    }

    @Input()
    set traceSpacing(traceSpacing: number) {
        this.ecgStore.dispatch(ecgViewerActions.traceSpacingMessage({ traceSpacing: traceSpacing }));
    }

    get isFitBaseline() {
        return this.ecgStore.selectSignal(selectorIsFitBaseline)();
    }

    @Input()
    set isFitBaseline(isFitBaseline: boolean) {
        this.ecgStore.dispatch(ecgViewerActions.baselineMessage({ isFitBaseline: isFitBaseline }));
    }

    get studySpacing() {
        return this.ecgStore.selectSignal(selectorStudySpacing)();
    }

    @Input()
    set studySpacing(studySpacing: number) {
        this.ecgStore.dispatch(ecgViewerActions.studySpacingMessage({ studySpacing: studySpacing }));
    }

    get horizontalStudySpacing$() {
        return this.ecgStore.pipe(select(selectorHorizontalStudySpacing));
    }

    get horizontalStudySpacing() {
        return this.ecgStore.selectSignal(selectorHorizontalStudySpacing)();
    }

    @Input()
    set horizontalStudySpacing(horizontalStudySpacing: number) {
        this.ecgStore.dispatch(
            ecgViewerActions.horizontalStudySpacingMessage({ horizontalStudySpacing: horizontalStudySpacing }),
        );
    }

    get zoomFactor() {
        return this.ecgStore.selectSignal(selectorZoomFactor)();
    }

    @Input()
    set zoomFactor(zoomFactor: number) {
        this.ecgStore.dispatch(ecgViewerActions.zoomFactorMessage({ zoomFactor: zoomFactor }));
    }

    get selectedFormat() {
        const format = this.ecgStore.selectSignal(selectorFormat)();
        return format;
    }

    @Input()
    set selectedFormat(selectedFormat: Format) {
        this.ecgStore.dispatch(ecgViewerActions.formatMessage({ format: selectedFormat }));
    }

    get summaryVisible() {
        return this.ecgStore.selectSignal(selectorSummaryVisible)();
    }

    @Input()
    set summaryVisible(summaryVisible: boolean) {
        this.ecgStore.dispatch(ecgViewerActions.summaryVisibleMessage({ summaryVisible: summaryVisible }));
    }

    get fiducialPointsVisible() {
        return this.ecgStore.selectSignal(selectorFiducialPointsVisible)();
    }

    @Input()
    set fiducialPointsVisible(fiducialPointsVisible: boolean) {
        this.ecgStore.dispatch(
            ecgViewerActions.fiducialPointsVisibleMessage({ fiducialPointsVisible: fiducialPointsVisible }),
        );
    }

    get printAsDisplayed() {
        return this.ecgStore.selectSignal(selectorPrintAsDisplayed)();
    }

    @Input()
    set printAsDisplayed(value: boolean) {
        this.ecgStore.dispatch(ecgViewerActions.printAsDisplayedMessage({ printAsDisplayed: value }));
    }

    get caliperMode() {
        return this.ecgStore.selectSignal(selectorCaliperMode)();
    }

    @Input()
    set caliperMode(caliperMode: CaliperMode) {
        this.ecgStore.dispatch(ecgViewerActions.caliperModeMessage({ caliperMode: caliperMode }));
    }

    private _selectedArrangementValue: ArrangementType | null = null;

    get selectedArrangementValue() {
        return this._selectedArrangementValue;
    }

    set selectedArrangementValue(arrangement: ArrangementType | null) {
        if (arrangement === null || this._selectedArrangementValue === arrangement) return;
        this._selectedArrangementValue = arrangement;

        const defaultGuidedComparison = ECG_CONSTANTS.defaultComparisonByArrangement[arrangement];

        this.selectedGuidedComparisonValue = defaultGuidedComparison;

        const arrangementObj = this.arrangements.find((item) => item.value === arrangement) ?? null;
        this._selectedArrangement = arrangementObj;
        this.ecgStore.dispatch(ecgViewerActions.arrangementMessage({ arrangement: arrangement }));
    }

    private _selectedArrangement: KeyValuePair<string, number> | null = null;

    get selectedArrangement() {
        return this._selectedArrangement;
    }

    @Input()
    set selectedArrangement(selectedArrangement) {
        if (!selectedArrangement) return;
        this.selectedArrangementValue = selectedArrangement.value;
    }

    get selectedTimeAlignment() {
        return this.ecgStore.selectSignal(selectorTimeAlignment)();
        //return this.store.getState().chartState.timeAlignment;
    }

    @Input()
    set selectedTimeAlignment(selectedTimeAlignment) {
        this.ecgStore.dispatch(ecgViewerActions.timeAlignmentMessage({ timeAlignment: selectedTimeAlignment }));
    }

    get selectedPaperRate() {
        return this.ecgStore.selectSignal(selectorPaperRate)();
    }

    @Input()
    set selectedPaperRate(selectedPaperRate) {
        this.ecgStore.dispatch(ecgViewerActions.paperRateMessage({ paperRate: selectedPaperRate }));
    }

    get customCommandState() {
        return this.ecgStore.selectSignal(selectorCustomCommand)();
    }

    @Input()
    set customCommandState(customCommandState) {
        this.ecgStore.dispatch(ecgViewerActions.customCommandMessage({ customCommand: customCommandState }));
    }

    get detailsVisible() {
        return this.ecgStore.selectSignal(selectorDetailsVisible)();
    }

    @Input()
    set detailsVisible(detailsVisible) {
        this.ecgStore.dispatch(ecgViewerActions.detailsVisibleMessage({ detailsVisible: detailsVisible }));
    }

    private _visualComparison = false;

    get visualComparison() {
        return this._visualComparison;
    }

    private _selectedGuidedComparisonValue: GuidedComparisonType | null = null;

    get selectedGuidedComparisonValue() {
        return this._selectedGuidedComparisonValue;
    }

    set selectedGuidedComparisonValue(guidedComparison: GuidedComparisonType | null) {
        if (guidedComparison === null || this._selectedGuidedComparisonValue === guidedComparison) return;
        this._selectedGuidedComparisonValue = guidedComparison;
        if (this._selectedGuidedComparisonValue === GuidedComparisonType.SideBySide) {
            /**@TODO cambiare logica */
            const arrangementToSelect =
                this.arrangements.find((arr) => arr.value === ArrangementType.TwelveXOne) ?? null;
            this.selectedArrangement = arrangementToSelect;
        }
        const guidedComparisonObj = this.guidedComparisons.find((item) => item.value === guidedComparison) ?? null;
        this._selectedGuidedComparison = guidedComparisonObj;

        const _guidedComparison: GuidedComparisonMessageParam = {
            traceSpacing: ECG_CONSTANTS.GUIDED_COMPARISON_TRACE_SPACING[guidedComparison],
            studySpacing: ECG_CONSTANTS.GUIDED_COMPARISON_STUDIES_SPACING[guidedComparison],
            horizontalStudySpacing: ECG_CONSTANTS.GUIDED_COMPARISON_STUDIES_HORIZONTAL_SPACING[guidedComparison],
            guidedComparison: guidedComparison,
        };

        this.ecgStore.dispatch(ecgViewerActions.guidedComparisonMessage({ guidedComparison: _guidedComparison }));
        this.ecgStore.dispatch(ecgViewerActions.fitMessage());
    }

    private _selectedGuidedComparison: KeyValuePair<string, number> | null = null;

    get selectedGuidedComparison() {
        return this._selectedGuidedComparison;
    }

    @Input()
    set selectedGuidedComparison(selectedGuidedComparisonObj) {
        if (!selectedGuidedComparisonObj) return;
        this.selectedGuidedComparisonValue = selectedGuidedComparisonObj.value;
    }

    _showCustomMeasuresButton: boolean = false;
    @Input() set showCustomMeasuresButton(value: boolean) {
        this._showCustomMeasuresButton = value;
    }

    _showGlobalMeasures: boolean = false;
    @Input() set showGlobalMeasures(value: boolean) {
        this._showGlobalMeasures = value;
        if (value && !this.selectedMeasures.includes(this.globalInputValue)) {
            this.selectedMeasures = [...this.selectedMeasures, this.globalInputValue];
        } else if (!value && this.selectedMeasures.includes(this.globalInputValue)) {
            let index = this.selectedMeasures.indexOf(this.globalInputValue);
            if (index !== -1) {
                this.selectedMeasures = this.selectedMeasures.filter((e) => e !== this.globalInputValue);
            }
        }
    }

    @Input() set showLeadsAnnotations(value: boolean) {
        this.isShowingLeads = value;
        if (value && !this.selectedMeasures.includes(this.leadInputValue)) {
            this.selectedMeasures = [...this.selectedMeasures, this.leadInputValue];
        } else if (!value && this.selectedMeasures.includes(this.leadInputValue)) {
            let index = this.selectedMeasures.indexOf(this.leadInputValue);
            if (index !== -1) {
                this.selectedMeasures = this.selectedMeasures.filter((e) => e !== this.leadInputValue);
            }
        }
    }

    private static initEnums(enumType: any, translate: TranslateService) {
        let array = new Array<KeyValuePair<string, number>>();
        let length = Object.keys(enumType).length;
        let keys = Object.keys(enumType).slice(length / 2, length);
        let values = Object.keys(enumType)
            .slice(0, length / 2)
            .map((key) => parseInt(key));

        for (let i = 0; i < length / 2; i++) array.push(new KeyValuePair(translate.instant(keys[i]), values[i]));
        return array;
    }

    private static initArrangementEnums(
        enumType: any,
        translate: TranslateService,
        arrangementConfiguration: IArrangementConfiguration,
    ) {
        if (arrangementConfiguration === null) return [];
        let array = LayoutFabToolsComponent.initEnums(enumType, translate);
        switch (arrangementConfiguration.leadCount) {
            case 15: // only XXOne, XXTwo and ThreeXFour
                array = array.slice(2, 3).concat(array.slice(6, 8));
                break;
            case 12: // remove XXOne, XXTwo
                array = array.slice(0, 6);
                // remove Median
                if (!arrangementConfiguration.derivedWaveformPresent) {
                    array = array.slice(0, array.length - 1);
                }
                break;
            default: // only XXOne, XXTwo
                array = array.slice(6, 8);
                break;
        }
        return array;
    }

    // public functions
    ngOnChanges(changes: SimpleChanges) {
        let change = changes['arrangementConfiguration'];
        if (change && change.currentValue !== change.previousValue) {
            this.updateSubToolsArray();
        }
        if (this._showCustomMeasuresButton && this._showGlobalMeasures) {
            this.onCustomMeasuresChange(true);
        }
    }

    ngAfterContentInit() {
        this.listenForEvents();
        this.updateSubToolsArray();
        this.applyEcgConfiguration();
    }

    ngOnDestroy() {
        this.storeSubscriber?.();
        this.translateSubscriber?.unsubscribe();
        this.removeHotKeys();
    }

    onCustomMeasuresChange(active: boolean) {
        this.customMeasuresChange.emit(active);
        //this.ecgStore.dispatch(ecgViewerActions.customMeasuresChange({ active: active }));
    }

    onLeadsAnnotationsChange(active: boolean) {
        this.leadsAnnotationsChange.emit(active);
    }

    onFit() {
        this.ecgStore.dispatch(ecgViewerActions.fitMessage());
    }

    onFullscreenChange() {
        this._isFullscreen = !this._isFullscreen;
        this.fullscreenChange.emit(this.isFullscreen);
    }

    onSelectedArrangement() {
        let index = !!this.selectedArrangement ? this.arrangements.indexOf(this.selectedArrangement) : -1;
        index++;
        if (index > this.arrangements.length - 1) index = 0;
        if (index < 0) index = 0;
        this.selectedArrangement = this.arrangements[index];
    }

    onSelectedFormat() {
        this.selectedFormat = this.selectedFormat == 0 ? 1 : 0;
    }

    onLeadsAnnotationsToggle() {
        if (this.selectedMeasures.includes(this.leadInputValue)) {
            this.selectedMeasures = this.selectedMeasures.filter((e) => e !== this.leadInputValue);
            this.onLeadsAnnotationsChange(false);
        } else {
            this.selectedMeasures = [...this.selectedMeasures, this.leadInputValue];
            this.onLeadsAnnotationsChange(true);
        }
    }

    onGlobalAnnotationsToggle() {
        if (this.selectedMeasures.includes(this.globalInputValue)) {
            this.selectedMeasures = this.selectedMeasures.filter((e) => e !== this.globalInputValue);
            this.onCustomMeasuresChange(false);
        } else {
            this.selectedMeasures = [...this.selectedMeasures, this.globalInputValue];
            this.onCustomMeasuresChange(true);
        }
    }

    onSelectedMeasures() {
        const global = this.selectedMeasures.includes(this.globalInputValue);
        this.onCustomMeasuresChange(global);
        const lead = this.selectedMeasures.includes(this.leadInputValue);
        this.onLeadsAnnotationsChange(lead);
    }

    onPrintAsDisplayed() {
        this.printAsDisplayed = !this.printAsDisplayed;
    }

    onLowFilterChange(filter?: number) {
        if (!isNaN(filter)) {
            this.lowFilter = filter;
        } else {
            const currentIndex = this.schillerFiltersConfiguration.lowFilters.indexOf(this.lowFilter);
            const nextIndex = (currentIndex + 1) % this.schillerFiltersConfiguration.lowFilters.length;
            this.lowFilter = this.schillerFiltersConfiguration.lowFilters[nextIndex];
        }
        this.lowFilterChange.emit(this.lowFilter); //Nel caso gli mando la configurazione di tutti e due i filtri
    }

    onSelectedTimeAlignment() {
        this.selectedTimeAlignment = this.selectedTimeAlignment == 0 ? 1 : 0;
    }

    onSelectedPaperRate() {
        this.selectedPaperRate = this.selectedPaperRate == 0 ? 1 : 0;
    }

    onSelectedGuidedComparison() {
        let index = !!this.selectedGuidedComparison
            ? this.guidedComparisons.indexOf(this.selectedGuidedComparison)
            : -1;
        index++;
        if (index > this.guidedComparisons.length - 1) index = 0;
        if (index < 0) index = 0;

        this.selectedGuidedComparison = this.guidedComparisons[index];
    }

    onCaliperMode() {
        this.caliperMode = this.caliperMode == 0 ? 1 : 0;
    }

    onFiducialPoints() {
        this.fiducialPointsVisible = this.fiducialPointsVisible ? false : true;
    }

    // private functions

    onCustomCommand() {
        const fullState = this.ecgStore.selectSignal(selectorFullState)();
        this.postMessageService.send(new PostMessage(fullState, PostMessageType.stateChanged));
    }

    isHorizontalSpacingEnabled = (): boolean =>
        this.visualComparison &&
        this.chartLayersNumber === 2 &&
        this.selectedArrangement?.value === ArrangementType.TwelveXOne &&
        this.selectedGuidedComparison?.value === GuidedComparisonType.SideBySide;

    private listenForEvents() {
        this.translateSubscriber = this.translate.onLangChange.subscribe(() => {
            let arrangementsIndex = !!this.selectedArrangement
                ? this.arrangements.indexOf(this.selectedArrangement)
                : -1;
            this.updateSubToolsArray();
            this.selectedArrangement = this.arrangements[arrangementsIndex];
        });

        this.actions.pipe(ofType(ecgViewerActions.visualComparisonMessage)).subscribe((action) => {
            this._visualComparison = action.visualComparison;
        });
    }

    private updateSubToolsArray() {
        this.arrangements = LayoutFabToolsComponent.initArrangementEnums(
            ArrangementType,
            this.translate,
            this.arrangementConfiguration,
        );
        if (this.arrangements && this.arrangements.length > 0) this.selectedArrangement = this.arrangements[0];
        this.guidedComparisons = LayoutFabToolsComponent.initEnums(GuidedComparisonType, this.translate);
        if (this.guidedComparisons && this.guidedComparisons.length > 0)
            this.selectedGuidedComparison = this.guidedComparisons[0]; // default: TraceToTrace
    }

    private addHotKeys() {
        this.hkFullScreen = this.hotkeysService.add(
            new Hotkey('ctrl+1', () => {
                this.onFullscreenChange();
                return true;
            }),
        );
        this.hkArrangement = this.hotkeysService.add(
            new Hotkey('ctrl+2', () => {
                this.onSelectedArrangement();
                return true;
            }),
        );
        this.hkFormat = this.hotkeysService.add(
            new Hotkey('ctrl+3', () => {
                this.onSelectedFormat();
                return true;
            }),
        );
        this.hkTime = this.hotkeysService.add(
            new Hotkey('ctrl+4', () => {
                this.onSelectedTimeAlignment();
                return true;
            }),
        );
        this.hkRate = this.hotkeysService.add(
            new Hotkey('ctrl+5', () => {
                this.onSelectedPaperRate();
                return true;
            }),
        );
        this.hkCaliperMode = this.hotkeysService.add(
            new Hotkey('ctrl+6', () => {
                this.onCaliperMode();
                return true;
            }),
        );
        this.hkFiducialPoints = this.hotkeysService.add(
            new Hotkey('ctrl+7', () => {
                this.onFiducialPoints();
                return true;
            }),
        );
        this.hkOpacityUp = this.hotkeysService.add(
            new Hotkey('alt+o+up', () => {
                if (this.paperOpacity < 1) this.paperOpacity += 0.1;
                else this.paperOpacity = 1;
                return true;
            }),
        );
        this.hkOpacityDown = this.hotkeysService.add(
            new Hotkey('alt+o+down', () => {
                if (this.paperOpacity > 0) this.paperOpacity -= 0.1;
                else this.paperOpacity = 0;
                return true;
            }),
        );
        this.hkTraceSpacingUp = this.hotkeysService.add(
            new Hotkey('alt+t+up', () => {
                if (this.traceSpacing < 2) this.traceSpacing += 0.005;
                else this.traceSpacing = 2;
                return true;
            }),
        );
        this.hkTraceSpacingDown = this.hotkeysService.add(
            new Hotkey('alt+t+down', () => {
                if (this.traceSpacing > 0) this.traceSpacing -= 0.005;
                else this.traceSpacing = 0;
                return true;
            }),
        );
        this.hkStudySpacingUp = this.hotkeysService.add(
            new Hotkey('alt+s+up', () => {
                if (this.studySpacing < 2) this.studySpacing += 0.005;
                else this.studySpacing = 2;
                return true;
            }),
        );
        this.hkStudySpacingDown = this.hotkeysService.add(
            new Hotkey('alt+s+down', () => {
                if (this.studySpacing > 0) this.studySpacing -= 0.005;
                else this.studySpacing = 0;
                return true;
            }),
        );
    }

    private removeHotKeys() {
        this.hotkeysService.remove(this.hkFullScreen ?? undefined);
        this.hotkeysService.remove(this.hkArrangement ?? undefined);
        this.hotkeysService.remove(this.hkFormat ?? undefined);
        this.hotkeysService.remove(this.hkTime ?? undefined);
        this.hotkeysService.remove(this.hkRate ?? undefined);
        this.hotkeysService.remove(this.hkCaliperMode ?? undefined);
        this.hotkeysService.remove(this.hkFiducialPoints ?? undefined);
        this.hotkeysService.remove(this.hkOpacityUp ?? undefined);
        this.hotkeysService.remove(this.hkOpacityDown ?? undefined);
        this.hotkeysService.remove(this.hkTraceSpacingUp ?? undefined);
        this.hotkeysService.remove(this.hkTraceSpacingDown ?? undefined);
        this.hotkeysService.remove(this.hkStudySpacingUp ?? undefined);
        this.hotkeysService.remove(this.hkStudySpacingDown ?? undefined);
    }

    private async applyEcgConfiguration() {
        if (!this.appConfig.isLoaded) await this.appConfig.load('./assets/config.json');

        let cultureName = this.appConfig.getValue('culture');
        this.culture =
            this.ecgConfiguration != null && this.ecgConfiguration.culture.read
                ? this.ecgConfiguration.culture.value
                : this.cultureMappingService.getCultureTypeByCultureName(cultureName);

        if (this.ecgConfiguration == null) return;

        if (this.ecgConfiguration.detailsvisible.read) this.detailsVisible = this.ecgConfiguration.detailsvisible.value;

        if (this.ecgConfiguration.layoutConfiguration != null) {
            if (this.ecgConfiguration.layoutConfiguration.arrangement.read)
                this.selectedArrangement =
                    this.arrangements.find(
                        (arrangement) =>
                            arrangement.value == this.ecgConfiguration.layoutConfiguration.arrangement.value,
                    ) ?? null;
            if (this.ecgConfiguration.layoutConfiguration.paperRate.read)
                this.selectedPaperRate = this.ecgConfiguration.layoutConfiguration.paperRate.value;
            if (this.ecgConfiguration.layoutConfiguration.paperOpacity.read)
                this.paperOpacity = this.ecgConfiguration.layoutConfiguration.paperOpacity.value;
        }

        if (this.ecgConfiguration.customCommandConfiguration != null) {
            let customCommandState = new CustomCommandState();

            if (this.ecgConfiguration.customCommandConfiguration.visible.read)
                customCommandState.visible = this.ecgConfiguration.customCommandConfiguration.visible.value;
            if (this.ecgConfiguration.customCommandConfiguration.text.read)
                customCommandState.text = this.ecgConfiguration.customCommandConfiguration.text.value;
            if (this.ecgConfiguration.customCommandConfiguration.source.read)
                customCommandState.source = this.ecgConfiguration.customCommandConfiguration.source.value;

            this.customCommandState = customCommandState;
        }
    }
}
